import { PatchAppointmentStatus } from "@/services/fhir/appointment/PatchAppointmentStatus";
import type { ProviderAgenda } from "../../affiliate/services/providerAgendaService";
import type { InviteKind } from "../../invites/constants";
import { guestPractitionerRoleReference } from "../../invites/helpers/practitionerRoleReferences";
import { InviteActionError } from "../../invites/services/inviteActionError";
import { createInviteTask } from "../../invites/services/inviteTaskService";
import { buildInviteTask } from "../../invites/transformers/inviteTaskTransformers";
import type { TeleconsultoriaGuest } from "../../types";

export const BOOKING_INVITE_FAILED_MESSAGE =
  "Não foi possível enviar o convite ao teleconsultor. A reserva foi desfeita; tente novamente.";

interface InviteTeleconsultorForBookingParams {
  appointment: fhir5.Appointment;
  agenda: ProviderAgenda;
  /** `<oid>-<cnpj>` da prestadora (identifier da Task e PractitionerRole do convidado). */
  providerToken: string;
  hostRoleReference: string;
  hostDisplay: string;
  inviteKind: Extract<InviteKind, "scheduled" | "now">;
}

/** Best effort: reserva sem convite não pode ficar ocupando o horário. */
async function cancelBooking(appointmentId: string) {
  try {
    await PatchAppointmentStatus({ appointmentId, status: "cancelled" });
  } catch (error) {
    console.error("inviteTeleconsultorForBooking.cancelBooking", appointmentId, error);
  }
}

/**
 * Convite (Task) da reserva para o PractitionerRole do teleconsultor na prestadora.
 * O teleconsultor só entra no Appointment quando ACEITAR (mesmo fluxo do convite).
 * Falhou: cancela a reserva (libera o horário) e lança erro com mensagem pronta.
 */
export async function inviteTeleconsultorForBooking({
  appointment,
  agenda,
  providerToken,
  hostRoleReference,
  hostDisplay,
  inviteKind,
}: InviteTeleconsultorForBookingParams): Promise<fhir5.Task> {
  if (!appointment.id) throw new Error("Appointment sem id");

  const guest: TeleconsultoriaGuest = {
    practitioner: agenda.teleconsultor,
    ...(agenda.healthcareService ? { healthcareService: agenda.healthcareService } : {}),
    organizationId: providerToken,
  };
  const guestRoleReference = guestPractitionerRoleReference(guest);

  try {
    if (!guestRoleReference || !hostRoleReference) {
      throw new Error("PractitionerRole do convite incompleto");
    }
    return await createInviteTask(
      buildInviteTask({
        appointment,
        guest,
        guestRoleReference,
        hostRoleReference,
        hostDisplay,
        inviteKind,
      }),
    );
  } catch (error) {
    console.error("inviteTeleconsultorForBooking", error);
    await cancelBooking(appointment.id);
    throw new InviteActionError(BOOKING_INVITE_FAILED_MESSAGE);
  }
}
